import { FC, useContext, useEffect } from 'react';
import { LogIn } from 'lucide-react';
import { Button } from '@socio-legal/react-components';

import { useAuth } from 'hooks/useAuth';
import { useTranslate } from 'hooks/useTranslate';

import ThemeContext from 'modules/_shared/contexts/ThemeContext';
import Slider from 'modules/auth/components/Slider';
import { getLoginByCorporateId } from 'modules/auth/utils/getLoginByCorporateId';

import 'assets/styles/Pages.scss';
import 'assets/styles/Styles.scss';

import { hasNewUI } from 'utils/showDetails';
import { applyBrandFont } from 'utils/theme';
import LoginFooter from './LoginFooter';
import LoginHeader from './LoginHeader';
import LoginWarning from './LoginWarning';
import LoginSlider from './LoginSlider';
import LoginSliderMobile from './LoginSliderMobile';
import LoginThemeToggle from './LoginThemeToggle';

type LoginProps = {
  blockedMode?: boolean;
  customStyle: {
    societyName: string;
    pageStyle: string;
    button: {
      icon: string;
      text: string;
    };
    warning: {
      text: string;
      email: string;
    };
    logo: string;
    accessTitle: string;
    accessText: string;
  };
};

const Login: FC<LoginProps> = ({ blockedMode, customStyle }) => {
  const { t } = useTranslate();
  const { login } = useAuth();
  const { darkTheme } = useContext(ThemeContext);

  const { pageStyle, button, warning } = customStyle || getLoginByCorporateId();

  useEffect(() => {
    if (hasNewUI()) {
      applyBrandFont();
    }
  }, []);

  return (
    <>
      <LoginThemeToggle />
      <div className={`nk-split nk-split-page nk-split-md ${pageStyle}`}>
        <div className="nk-split-content nk-block-area nk-block-area-column nk-auth-container bg-white">
          <div className="nk-block nk-block-middle nk-auth-body">
            <LoginHeader blockedMode={blockedMode} customStyle={customStyle} />

            {!blockedMode && (
              <>
                {hasNewUI() ? (
                  <Button
                    aria-label="Login"
                    className="btn-login"
                    icon={LogIn}
                    onClick={login}
                    size="lg"
                    variant="primary"
                    fullWidth
                  >
                    {t(button?.text || 'Login')}
                  </Button>
                ) : (
                  <button
                    className="btn btn-lg btn-primary btn-block btn-login"
                    type="button"
                    aria-label="Login"
                    onClick={login}
                  >
                    <em className={`icon ni mr-1 ${button?.icon}`} />
                    {t(button?.text || 'Login')}
                  </button>
                )}

                {hasNewUI() && (
                  <LoginSliderMobile
                    dark={darkTheme}
                    className="d-lg-none"
                  />
                )}
              </>
            )}

            <LoginWarning warning={warning} />
          </div>

          <LoginFooter customStyle={customStyle} />
        </div>

        {hasNewUI() ? (
          <div className="d-none d-lg-flex" style={{ flex: 1 }}>
            <LoginSlider dark={darkTheme} />
          </div>
        ) : (
          <Slider />
        )}
      </div>
    </>
  );
};

export default Login;
